import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../config/firebase.js';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import GoogleAnalytics from '../../analyticsComponents/GoogleAnalytics.js';
import GRADES from '../GradePage/grades.json';
import toPascalCase from '../../utils/toPascalCase';
import logger from '../../utils/logger';

// Build all grade+subject combos (excluding Teachers) for direct collection queries
const gradeSubjectPairs = GRADES
  .filter(g => g.name !== 'Teachers')
  .flatMap(g => {
    const cleanGrade = g.name.trim().replace(/\s+/g, '');
    return g.subjects.map(s => ({
      grade: cleanGrade,
      subject: toPascalCase(s),
      displaySubject: s,
    }));
  });

function SearchComponent() {
  const [searchTerm, setSearchTerm] = useState('');
  const [allPDFs, setAllPDFs] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  useEffect(() => {
    // Use a Map keyed by "grade/subject/docId" to avoid duplicates
    const pdfMap = new Map();
    const unsubscribes = [];

    gradeSubjectPairs.forEach(({ grade, subject, displaySubject }) => {
      const colRef = collection(db, 'PDFS', grade, subject);
      const q = query(colRef, where('verified', '==', true));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const key = `${grade}/${subject}/${change.doc.id}`;
          const pdfData = {
            ...change.doc.data(),
            id: change.doc.id,
            _subject: displaySubject,
            _grade: grade,
          };

          if (change.type === 'added' || change.type === 'modified') {
            pdfMap.set(key, pdfData);
          }
          if (change.type === 'removed') {
            pdfMap.delete(key);
          }
        });

        // Rebuild the array from the map (single source of truth)
        setAllPDFs(Array.from(pdfMap.values()));
      }, (error) => {
        // Silently ignore collections that don't exist yet
        if (error.code !== 'permission-denied') {
          logger.error(`Error fetching PDFs for ${grade}/${subject}:`, error);
        }
      });

      unsubscribes.push(unsubscribe);
    });

    return () => unsubscribes.forEach(unsub => unsub());
  }, []);

  // Suggestions logic
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSuggestions([]);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = allPDFs.filter(pdf => {
      const titleMatch = pdf.title?.toLowerCase().includes(term);
      const tagsMatch = Array.isArray(pdf.tags) &&
        pdf.tags.some(tag => tag.toLowerCase().includes(term));
      // Match against the derived subject name (e.g. "Business" matches "business")
      const subjectMatch = pdf._subject?.toLowerCase().includes(term);
      return titleMatch || tagsMatch || subjectMatch;
    });

    setSuggestions(filtered.slice(0, 5));
  }, [searchTerm, allPDFs]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    setSuggestions([]);
    navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
  };

  const handleSuggestionClick = (pdf) => {
    setSuggestions([]);
    if (pdf.file_url) {
      window.open(pdf.file_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={searchRef} className="relative w-full max-w-2xl mx-auto">
      <GoogleAnalytics page="SearchComponentPage" eventType="searchComponent_page" />
      <form className="flex shadow-card rounded-xl overflow-hidden" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="Search resources by keywords..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-5 py-3 border border-gray-200 border-r-0 rounded-l-xl focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-700 text-sm"
        />
        <button
          type="submit"
          className="px-5 py-3 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-r-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-400 text-sm"
        >
          Search
        </button>
      </form>

      {suggestions.length > 0 && (
        <ul className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 max-h-60 overflow-y-auto z-50 rounded-md shadow-lg">
          {suggestions.map(pdf => (
            <li
              key={pdf.id}
              className="px-4 py-2 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => handleSuggestionClick(pdf)}
            >
              <span className="text-gray-800 block font-medium">{pdf.title}</span>
              <span className="text-gray-400 text-xs">{pdf._subject}{pdf.grade ? ` · Grade ${pdf.grade}` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SearchComponent;
